import { useMemo, useState } from 'react'
import { TrendingUp, TrendingDown, Users, DollarSign, Activity, ShoppingCart, Loader2, CreditCard, LayoutDashboard, MessageCircle, CalendarDays } from 'lucide-react'
import { SkeletonPage } from '@/components/ui/Skeleton'
import { useEventData } from '@/contexts/EventDataContext'
import { useAuth } from '@/contexts/AuthContext'
import { useOperator } from '@/contexts/OperatorContext'
import { Link } from 'react-router-dom'
import { ChatInterface } from '@/components/ChatInterface'
import { DataAnchoredInvitations } from '@/components/home/DataAnchoredInvitations'
import { DaySwitcher } from '@/components/DaySwitcher'

function fmt(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`
  return `$${n.toLocaleString()}`
}

function fmtCents(cents: number): string {
  return fmt(Math.round(cents / 100))
}

function fmtNum(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

function YoyBadge({ value, label }: { value?: number; label?: string }) {
  if (value == null) return null
  const positive = value >= 0
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold ${
        positive ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
      }`}
      title={label}
    >
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? '+' : ''}{value.toFixed(1)}%
    </span>
  )
}

export function HomePage() {
  const { short: brandName } = useOperator()
  const [mobileTab, setMobileTab] = useState<'dashboard' | 'chat'>('dashboard')
  const { currentEventId } = useAuth()
  const {
    stats, extras, intelligence, revenueBreakdown, yoy,
    selectedDate, eventDays, yoyDay, dayStats, dayLoading,
    loading,
  } = useEventData()

  const activeStats = selectedDate ? dayStats : stats
  const activeYoy = selectedDate ? yoyDay?.deltas ?? null : yoy

  const priorYearLabel = useMemo(() => {
    if (!selectedDate || !yoyDay?.prior_year) return undefined
    return `vs ${yoyDay.prior_year.event_name} (${yoyDay.prior_year.date})`
  }, [selectedDate, yoyDay])

  const topPlans = useMemo(() => {
    if (!extras?.plan_mix) return []
    const maxRev = Math.max(...extras.plan_mix.map(p => p.revenue), 1)
    return extras.plan_mix.slice(0, 6).map(p => ({
      name: p.plan_name,
      revenue: p.revenue,
      orders: p.orders,
      pct: Math.round((p.revenue / maxRev) * 100),
    }))
  }, [extras])

  const activeHourlyMetrics = useMemo(() => {
    const source = activeStats?.hourly
    if (!source?.length) return null
    const active = source.filter(h => h.orders >= 2)
    if (active.length === 0) return null
    const totalOrders = active.reduce((s, h) => s + h.orders, 0)
    const totalRiders = active.reduce((s, h) => s + h.riders, 0)
    return {
      ordersPerHour: Math.round(totalOrders / active.length),
      ridersPerHour: Math.round(totalRiders / active.length),
      activeHours: active.length,
    }
  }, [activeStats])

  const insightCards = useMemo(() => {
    return intelligence?.insights?.slice(0, 3) ?? []
  }, [intelligence])

  const heatmapGrid = useMemo(() => {
    if (!intelligence?.heatmap) return null
    const { attractions, hours, max_scans } = intelligence.heatmap
    return { attractions: attractions.slice(0, 12), hours, max_scans }
  }, [intelligence])

  const revenueSparkBars = useMemo(() => {
    const source = activeStats?.hourly
    if (!source?.length) return []
    const maxRev = Math.max(...source.map(h => h.revenue), 1)
    return source.map(h => ({
      hour: h.hour,
      revenue: h.revenue,
      pct: Math.round((h.revenue / maxRev) * 100),
    }))
  }, [activeStats])

  const eventToDateGross = useMemo(() => {
    if (!eventDays?.days?.length) return null
    return eventDays.days.reduce((sum, d) => sum + d.revenue, 0)
  }, [eventDays])

  if (currentEventId == null) {
    return (
      <div className="p-6 max-w-6xl mx-auto">
        <div className="rounded-lg border border-dashed border-slate-300 p-12 text-center">
          <h2 className="font-display font-bold text-lg text-ink-dark mb-2">Welcome to {brandName}</h2>
          <p className="text-sm text-ink-soft">Select an event from the sidebar to see your dashboard.</p>
        </div>
      </div>
    )
  }

  if (loading && !stats) {
    return <SkeletonPage />
  }

  const m = activeStats?.metrics
  const eventName = stats?.event?.event_name ?? 'Event'
  const showDayView = selectedDate != null
  const isLoadingDay = showDayView && dayLoading

  return (
    <div className={`flex flex-col ${mobileTab === 'chat' ? 'h-full' : ''} lg:h-auto lg:block`}>
      {/* Mobile tab bar */}
      <div className="lg:hidden flex shrink-0 border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900">
        <button
          type="button"
          onClick={() => setMobileTab('dashboard')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
            mobileTab === 'dashboard'
              ? 'text-butler-700 border-b-2 border-butler-600 dark:text-butler-200'
              : 'text-ink-soft hover:text-ink-dark dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <LayoutDashboard className="h-4 w-4" />
          Dashboard
        </button>
        <button
          type="button"
          onClick={() => setMobileTab('chat')}
          className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-semibold transition-colors ${
            mobileTab === 'chat'
              ? 'text-butler-700 border-b-2 border-butler-600 dark:text-butler-200'
              : 'text-ink-soft hover:text-ink-dark dark:text-slate-400 dark:hover:text-slate-200'
          }`}
        >
          <MessageCircle className="h-4 w-4" />
          Chat
        </button>
      </div>

      {mobileTab === 'chat' && (
        <div className="lg:hidden flex flex-col flex-1 min-h-0 overflow-hidden">
          <DataAnchoredInvitations />
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            <ChatInterface />
          </div>
        </div>
      )}

      <div className={`${mobileTab === 'chat' ? 'hidden' : ''} lg:block p-6 max-w-6xl mx-auto space-y-6`}>
        {/* DaySwitcher */}
        <section className="mm-card-v3 p-4">
          <div className="flex items-center gap-2 mb-3">
            <CalendarDays className="h-4 w-4 text-butler-600" />
            <h2 className="font-display font-semibold text-sm text-ink-dark">
              {showDayView ? `Day View — ${new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}` : 'Event Overview'}
            </h2>
            {priorYearLabel && (
              <span className="text-[10px] text-ink-soft ml-auto">{priorYearLabel}</span>
            )}
          </div>
          <DaySwitcher />
        </section>

        {/* Hero KPI Card */}
        <div className="mm-card-v3 p-6 relative">
          {isLoadingDay && (
            <div className="absolute top-3 right-3">
              <Loader2 className="h-4 w-4 animate-spin text-butler-400" />
            </div>
          )}
          <div className="flex items-start justify-between mb-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-ink-soft">
                {showDayView ? "Today's Revenue" : 'Gross Revenue'}
              </p>
              <h1 className="font-display font-bold text-3xl text-ink-dark mt-1">
                {m ? fmt(m.revenue_total) : '—'}
              </h1>
              <div className="flex items-center gap-2 mt-1">
                <YoyBadge value={activeYoy?.revenue_total} label={priorYearLabel} />
                {showDayView && yoyDay?.prior_year && (
                  <span className="text-[10px] text-ink-soft">
                    vs {fmt(yoyDay.prior_year.metrics.revenue_total)} last year
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <span className="text-xs text-ink-soft font-medium">{eventName}</span>
              {eventToDateGross != null && !showDayView && (
                <p className="text-[10px] text-ink-soft mt-0.5">Event total: {fmt(eventToDateGross)}</p>
              )}
              {eventToDateGross != null && showDayView && (
                <p className="text-[10px] text-ink-soft mt-0.5">Event-to-date: {fmt(eventToDateGross)}</p>
              )}
            </div>
          </div>
          {revenueSparkBars.length > 0 && (
            <div className="flex items-end gap-[2px] h-16">
              {revenueSparkBars.map(b => (
                <div
                  key={b.hour}
                  className="flex-1 rounded-t-sm bg-butler-500/80"
                  style={{ height: `${Math.max(b.pct, 2)}%` }}
                  title={`${b.hour}:00 — ${fmt(b.revenue)}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* 4 KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            icon={<Users className="h-4 w-4" />}
            label="Guests"
            value={m ? fmtNum(m.guests) : '—'}
            yoy={activeYoy?.guests}
            yoyLabel={priorYearLabel}
          />
          <KpiCard
            icon={<DollarSign className="h-4 w-4" />}
            label="Avg / Guest"
            value={m && m.guests > 0 ? `$${(m.revenue_total / m.guests).toFixed(2)}` : '—'}
          />
          <KpiCard
            icon={<Activity className="h-4 w-4" />}
            label="Riders / Active Hr"
            value={activeHourlyMetrics ? fmtNum(activeHourlyMetrics.ridersPerHour) : m ? fmtNum(m.riders_per_hour) : '—'}
            subtitle={activeHourlyMetrics ? `${activeHourlyMetrics.activeHours} active hrs` : undefined}
            yoy={activeYoy?.riders_per_hour}
            yoyLabel={priorYearLabel}
          />
          <KpiCard
            icon={<ShoppingCart className="h-4 w-4" />}
            label="Orders / Active Hr"
            value={activeHourlyMetrics ? fmtNum(activeHourlyMetrics.ordersPerHour) : m ? fmtNum(m.orders_per_hour) : '—'}
            subtitle={activeHourlyMetrics ? `${activeHourlyMetrics.activeHours} active hrs` : undefined}
            yoy={activeYoy?.orders_per_hour}
            yoyLabel={priorYearLabel}
          />
        </div>

        {/* Revenue Composition Strip — only on event overview */}
        {!showDayView && revenueBreakdown && revenueBreakdown.purchase_breakdown.some(p => p.cents > 0) && (() => {
          const total = revenueBreakdown.revenue.total_gross_cents || 1
          const items = revenueBreakdown.purchase_breakdown.filter(p => p.cents > 0)
          const colors = ['#0070ab', '#00c7ca', '#ed00be', '#f59e0b']
          return (
            <section className="mm-card-v3 p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-butler-600" />
                  <h2 className="font-display font-semibold text-sm text-ink-dark">Revenue Composition</h2>
                </div>
                <Link to="/revenue" className="text-xs text-butler-600 hover:underline">View details →</Link>
              </div>
              <div className="flex h-3 rounded-full overflow-hidden mb-2">
                {items.map((p, i) => (
                  <div
                    key={p.type}
                    style={{ width: `${(p.cents / total) * 100}%`, backgroundColor: colors[i % colors.length] }}
                    title={`${p.label}: ${fmtCents(p.cents)}`}
                  />
                ))}
              </div>
              <div className="flex items-center gap-4 text-xs text-ink-soft">
                {items.map((p, i) => (
                  <div key={p.type} className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: colors[i % colors.length] }} />
                    <span>{p.label}: {fmtCents(p.cents)}</span>
                  </div>
                ))}
                {revenueBreakdown.breakage.total_cents > 0 && (
                  <div className="flex items-center gap-1.5">
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
                    <span>Breakage: {fmtCents(revenueBreakdown.breakage.total_cents)}</span>
                  </div>
                )}
              </div>
            </section>
          )
        })()}

        {/* Top Plans + Insight Cards side by side — event overview only */}
        {!showDayView && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="mm-card-v3 p-5">
              <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Top Plans</h2>
              {topPlans.length === 0 ? (
                <div className="py-6 text-center text-xs text-ink-soft">No plan data yet.</div>
              ) : (
                <div className="space-y-2.5">
                  {topPlans.map(p => (
                    <div key={p.name} className="flex items-center gap-3">
                      <span className="w-28 text-xs text-ink-soft shrink-0 truncate" title={p.name}>{p.name}</span>
                      <div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden dark:bg-slate-800">
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${p.pct}%`, background: 'linear-gradient(90deg, #0070ab, #00c7ca)' }}
                        />
                      </div>
                      <span className="mm-data text-xs font-medium text-ink-dark w-16 text-right">
                        {fmt(p.revenue)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="space-y-3">
              <h2 className="font-display font-semibold text-sm text-ink-dark">Insights</h2>
              {insightCards.length === 0 ? (
                <div className="mm-card-v3 p-5 text-center text-xs text-ink-soft">
                  Intelligence data loading…
                </div>
              ) : (
                insightCards.map((card, i) => (
                  <div key={i} className="mm-card-v3 p-4 flex items-start gap-3">
                    <span className="text-xl shrink-0">{card.icon}</span>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-ink-dark">{card.title}</div>
                      <p className="text-xs text-ink-soft mt-1 leading-relaxed">{card.body}</p>
                    </div>
                  </div>
                ))
              )}
            </section>
          </div>
        )}

        {/* Activity Heat Map — event overview only */}
        {!showDayView && heatmapGrid && heatmapGrid.attractions.length > 0 && (
          <section className="mm-card-v3 p-5">
            <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Activity Heat Map</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr>
                    <th className="py-1 px-2 text-left font-medium text-ink-soft w-32">Attraction</th>
                    {heatmapGrid.hours.map(h => (
                      <th key={h} className="py-1 px-1 text-center font-medium text-ink-soft w-8">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {heatmapGrid.attractions.map(a => (
                    <tr key={a.name}>
                      <td className="py-1 px-2 text-ink-dark truncate max-w-[120px]" title={a.name}>{a.name}</td>
                      {heatmapGrid.hours.map(h => {
                        const val = a.hours[h] ?? 0
                        const intensity = heatmapGrid.max_scans > 0 ? val / heatmapGrid.max_scans : 0
                        return (
                          <td key={h} className="py-1 px-1">
                            <div
                              className="w-6 h-6 rounded-sm mx-auto"
                              style={{
                                backgroundColor: intensity > 0
                                  ? `rgba(0, 112, 171, ${0.1 + intensity * 0.85})`
                                  : 'rgba(0,0,0,0.03)',
                              }}
                              title={`${a.name} @ ${h}:00 — ${val} scans`}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {/* Capacity Utilization — event overview only */}
        {!showDayView && intelligence?.attraction_deep && intelligence.attraction_deep.length > 0 && (
          <section className="mm-card-v3 p-5">
            <h2 className="font-display font-semibold text-sm text-ink-dark mb-4">Ride Capacity Utilization</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {intelligence.attraction_deep
                .filter(a => a.type === 'Rides' && a.cycles_per_hour != null)
                .slice(0, 8)
                .map(a => {
                  const avgUtil = a.capacity_utilization.length > 0
                    ? Math.round(a.capacity_utilization.reduce((s, u) => s + u.pct, 0) / a.capacity_utilization.length)
                    : 0
                  return (
                    <div key={a.attraction_id} className="text-center">
                      <div className="relative w-16 h-16 mx-auto">
                        <svg viewBox="0 0 36 36" className="w-full h-full">
                          <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e2e8f0" strokeWidth="3" />
                          <circle
                            cx="18" cy="18" r="15.9"
                            fill="none"
                            stroke={avgUtil >= 70 ? '#0070ab' : avgUtil >= 40 ? '#f59e0b' : '#ef4444'}
                            strokeWidth="3"
                            strokeDasharray={`${avgUtil} ${100 - avgUtil}`}
                            strokeDashoffset="25"
                            strokeLinecap="round"
                          />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-ink-dark">
                          {avgUtil}%
                        </span>
                      </div>
                      <p className="text-xs text-ink-dark mt-2 font-medium truncate" title={a.name}>{a.name}</p>
                      {a.est_wait_avg_sec != null && (
                        <p className="text-[10px] text-ink-soft">~{Math.round(a.est_wait_avg_sec / 60)}min wait</p>
                      )}
                    </div>
                  )
                })}
            </div>
          </section>
        )}
      </div>
    </div>
  )
}

function KpiCard({ icon, label, value, subtitle, yoy, yoyLabel }: {
  icon: React.ReactNode
  label: string
  value: string
  subtitle?: string
  yoy?: number
  yoyLabel?: string
}) {
  return (
    <div className="mm-card-v3 p-4">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-butler-600">{icon}</span>
        <span className="text-xs font-medium uppercase tracking-wide text-ink-soft">{label}</span>
      </div>
      <div className="font-display font-bold text-xl text-ink-dark">{value}</div>
      {subtitle && <p className="text-[10px] text-ink-soft mt-0.5">{subtitle}</p>}
      {yoy != null && <YoyBadge value={yoy} label={yoyLabel} />}
    </div>
  )
}
